import type { Breadcrumb, BreadcrumbHint } from "@sentry/vue";

/**
 * Handle Sentry breadcrumbs before they are sent
 *
 * - Improves click event element detection to find closest relevant interactable element
 */
export const handleSentryBeforeBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint,
): Breadcrumb | null => {
  if (breadcrumb.category === "ui.click") {
    const targetElement = hint?.event?.target as HTMLElement | undefined;
    if (!targetElement) return breadcrumb;

    const message = getClickBreadcrumbMessage(targetElement);
    breadcrumb.message = message ?? breadcrumb.message;
  }
  return breadcrumb;
};

/**
 * Creates Sentry breadcrumb message from target/clicked element OR valid parent element.
 *
 * Finds the closest ancestor element (or self) with expected breadcrumb indicators (see below)
 *
 * - `id` attribute
 * - `data-sentry` attribute
 */
const getClickBreadcrumbMessage = (targetElement: HTMLElement | undefined): string | null => {
  if (!targetElement) return null;

  // Target/clicked element may have the expected breadcrumb indicators (no need to check parents)
  const targetElementSentryMessage = getElementAttributesMessageForSentry(targetElement);
  if (targetElementSentryMessage) {
    return targetElementSentryMessage;
  }

  // Ensure target/clicked element attributes are still captured
  // TODO: Consider using default Sentry breadcrumb instead
  const targetElementMessage = getElementAttributesMessage(targetElement);

  let currentElement: HTMLElement | null = targetElement;
  const maxAncestors = 5;
  for (let i = 0; i < maxAncestors; i++) {
    currentElement = currentElement.parentElement;
    if (!currentElement) break;

    const currentElementSentryMessage = getElementAttributesMessageForSentry(currentElement);
    if (currentElementSentryMessage) {
      return targetElementMessage
        ? `${currentElementSentryMessage} => ${targetElementMessage}`
        : currentElementSentryMessage;
    }
  }

  return targetElementMessage;
};

/**
 * Get element attributes/data message for Sentry breadcrumb (requires ID or `data-sentry`)
 *
 * NOTE: Only returns attribute message for elements with ID or `data-sentry` attribute!
 */
const getElementAttributesMessageForSentry = (element: HTMLElement | undefined): string | null => {
  /** Some global IDs should be ignored */
  const ignoredIds = ["app"];

  const parentId = element?.id;
  const parentSentryAttribute = element?.dataset.sentry;
  if ((!parentId || ignoredIds.includes(parentId)) && !parentSentryAttribute) {
    return null;
  }

  return getElementAttributesMessage(element);
};

/**
 * Get element attributes/data message (intended for breadcrumb-items only)
 *
 * Includes element tag, ID, `data-sentry` attribute, classes, and several other attributes
 *
 * NOTE: Always returns attribute message, performing no validity/Sentry/ID checks
 */
const getElementAttributesMessage = (element: HTMLElement) => {
  const elementTag = element.tagName.toLowerCase();
  const elementTagId = element.id ? `#${element.id}` : "";
  const elementSentryAttribute = element.dataset.sentry
    ? `[data-sentry="${element.dataset.sentry}"]`
    : "";
  const elementAttributesToCheck = ["type"];
  const elementOtherAttributes = elementAttributesToCheck.reduce((accum, attribute) => {
    const value = element.getAttribute(attribute);
    return value ? `${accum}[${attribute}="${value}"]` : accum;
  }, "");
  // Only include classes if no ID or Sentry attribute were present
  const elementTagClasses =
    element.className.length && !elementTagId && !elementSentryAttribute
      ? `.${element.className.split(" ").join(".")}`
      : "";
  return [
    elementTag,
    elementTagId,
    elementSentryAttribute,
    elementOtherAttributes,
    elementTagClasses,
  ].join("");
};
